import AsyncStorage from '@react-native-async-storage/async-storage';

const PENDING_REFERRAL_CODE_KEY = '@salunbd/pending-referral-code';

/** Saves an incoming referral code so it can be automatically applied upon registration/login. */
export async function savePendingReferralCode(code: string): Promise<void> {
  if (!code || typeof code !== 'string') return;
  const clean = code.trim().toUpperCase();
  if (clean) {
    await AsyncStorage.setItem(PENDING_REFERRAL_CODE_KEY, clean).catch(() => undefined);
  }
}

/** Retrieves any pending referral code waiting to be redeemed. */
export async function getPendingReferralCode(): Promise<string | null> {
  try {
    return await AsyncStorage.getItem(PENDING_REFERRAL_CODE_KEY);
  } catch {
    return null;
  }
}

/** Clears the pending referral code once it has been redeemed. */
export async function clearPendingReferralCode(): Promise<void> {
  await AsyncStorage.removeItem(PENDING_REFERRAL_CODE_KEY).catch(() => undefined);
}

const INSTALL_REFERRER_CHECKED_KEY = '@salunbd/install-referrer-checked';

/** The shape of an invite code: letters and digits only (matches what the server hands out). */
export const REFERRAL_CODE_PATTERN = /^[A-Z0-9]{4,16}$/;

/**
 * Reads the invite code out of a Play install-referrer string such as
 * `invite_code=3FA9C21B&utm_source=salunbd_invite`. Returns null for anything else, including the
 * `utm_source=google-play&utm_medium=organic` string a normal store install carries.
 */
export function referralCodeFromInstallReferrer(referrer: string | null | undefined): string | null {
  if (!referrer) return null;
  try {
    const params = new URLSearchParams(referrer);
    const raw = (params.get('invite_code') || params.get('ref') || '').trim().toUpperCase();
    return REFERRAL_CODE_PATTERN.test(raw) ? raw : null;
  } catch {
    return null;
  }
}

/** True once the install referrer has been read on this device (it only ever needs reading once). */
export async function hasCheckedInstallReferrer(): Promise<boolean> {
  try {
    return (await AsyncStorage.getItem(INSTALL_REFERRER_CHECKED_KEY)) === '1';
  } catch {
    return false;
  }
}

export async function markInstallReferrerChecked(): Promise<void> {
  await AsyncStorage.setItem(INSTALL_REFERRER_CHECKED_KEY, '1').catch(() => undefined);
}
